import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "../components/FormField";
import { StatusBadge, eur, fmtDate } from "../helpers";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, Wallet, TrendingUp, BedDouble, Gauge, Percent, FileText, Trash2, Upload, Wifi, KeyRound, MapPin, Users, Bath, Copy, Activity, Coins, HelpCircle, DoorOpen, Phone } from "lucide-react";
import { useAuth } from "../AuthContext";
import ExpenseManager from "../components/ExpenseManager";
import EconomicDataEditor from "../components/EconomicDataEditor";
import WhySheet from "../components/WhySheet";
import InventoryTab from "../components/apt/InventoryTab";
import UtilitiesTab from "../components/apt/UtilitiesTab";
import IntegrationsTab from "../components/apt/IntegrationsTab";
import AutomationsTab from "../components/apt/AutomationsTab";
import MediaCenterTab from "../components/apt/MediaCenterTab";
import WelcomeCenterManager from "../components/welcome/WelcomeCenterManager";
import PhotoManager from "../components/PhotoManager";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONTHS_IT = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
export const AUTO_GATE_MESSAGE = "Per aprire il portone è sufficiente effettuare una chiamata al numero indicato. Il servizio è completamente gratuito, non utilizza WhatsApp, non richiede eSIM o SIM dedicate e non risponderà alcun operatore. La chiamata attiverà automaticamente l'apertura del portone.";

export default function ApartmentDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const now = new Date();
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [why, setWhy] = useState({ open: false, metric: null });
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get(`/apartments/${id}/detail`, { params: { year: period.year, month: period.month } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [id, period.year, period.month]);
  useEffect(() => { load(); }, [load]);

  const openWhy = (metric) => setWhy({ open: true, metric });
  const [editInfo, setEditInfo] = useState(false);
  const [infoForm, setInfoForm] = useState(null);
  const [hist, setHist] = useState(null);
  const [docMeta, setDocMeta] = useState({ category: "Contratto", expiry_date: "" });

  const startEdit = () => { setInfoForm({ ...d.apartment }); setEditInfo(true); };
  const setF = (k, v) => setInfoForm((f) => ({ ...f, [k]: v }));
  const saveInfo = async () => {
    try { await api.put(`/apartments/${id}`, infoForm); toast.success(t("saved")); setEditInfo(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const loadHistory = useCallback(() => {
    api.get(`/audit-logs`, { params: { apartment_id: id, page_size: 100 } })
      .then((r) => setHist(r.data.items || [])).catch(() => setHist([]));
  }, [id]);
  useEffect(() => { if (tab === "history" && hist === null) loadHistory(); }, [tab, hist, loadHistory]);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/apartments/${id}/documents`, { name: file.name, url: up.data.url, category: docMeta.category, expiry_date: docMeta.expiry_date });
      toast.success(t("saved")); setDocMeta({ category: "Contratto", expiry_date: "" }); load();
    } catch { toast.error("Upload error"); }
    e.target.value = "";
  };
  const delDoc = async (docId) => { await api.delete(`/apartments/${id}/documents/${docId}`); load(); };

  const duplicate = async () => {
    try {
      const r = await api.post(`/apartments/${id}/duplicate`);
      toast.success("Immobile duplicato · modifica i dati specifici");
      navigate(`/apartments?edit=${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella duplicazione"); }
  };

  // Duplica configurazione → immobile ESISTENTE (selettiva/completa)
  const [dupOpen, setDupOpen] = useState(false);
  const [dupTargets, setDupTargets] = useState([]);
  const [dupSectionsCat, setDupSectionsCat] = useState([]);
  const [dupTarget, setDupTarget] = useState("");
  const [dupSel, setDupSel] = useState([]);
  const [dupStep, setDupStep] = useState("select"); // select | confirm
  const [dupBusy, setDupBusy] = useState(false);
  const openDupConfig = async () => {
    setDupOpen(true); setDupStep("select"); setDupTarget(""); setDupSel([]);
    try {
      const [aps, secs] = await Promise.all([api.get("/apartments"), api.get("/apartments/duplicate-config/sections")]);
      setDupTargets((aps.data || []).filter((x) => x.id !== id));
      setDupSectionsCat(secs.data || []);
    } catch { toast.error("Errore caricamento dati duplica"); }
  };
  const toggleSec = (k) => setDupSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const allSelected = dupSectionsCat.length > 0 && dupSel.length === dupSectionsCat.length;
  const toggleAll = () => setDupSel(allSelected ? [] : dupSectionsCat.map((s) => s.key));
  const runDupConfig = async () => {
    setDupBusy(true);
    try {
      const r = await api.post(`/apartments/${id}/duplicate-config`, { target_id: dupTarget, sections: dupSel });
      toast.success(`Configurazione copiata · ${r.data.labels.length} sezioni`);
      setDupOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore duplica configurazione"); }
    finally { setDupBusy(false); }
  };
  const dupTargetName = dupTargets.find((x) => x.id === dupTarget);

  if (!d) return <div className="p-8 text-sm text-slate-400" data-testid="detail-loading">…</div>;
  const a = d.apartment;
  const e = d.economics;
  const isAdmin = ["super_admin", "admin", "amministratore"].includes(user?.role);

  const tabs = [
    { k: "overview", label: t("overview") },
    { k: "info", label: "Anagrafica" },
    { k: "documents", label: t("documents") },
    { k: "media", label: "Media Center" },
    { k: "welcome", label: "Welcome Center" },
    { k: "inventory", label: "Inventario" },
    { k: "utilities", label: "Utenze" },
    { k: "costs", label: t("apartmentCostsTab") },
    { k: "bookings", label: t("bookings") },
    { k: "maintenance", label: t("maintenance") },
    { k: "integrations", label: "Integrazioni" },
    { k: "automations", label: "Automazioni" },
    { k: "report", label: t("monthlyReport") },
    ...(isAdmin ? [{ k: "history", label: "Cronologia" }] : []),
  ];
  const kpi = (icon, label, value, tone, metric) => {
    const Icon = icon;
    return (
      <Card
        data-testid={`kpi-card-${metric}`}
        onClick={() => metric && openWhy(metric)}
        className={`group relative flex items-center gap-3 border-slate-200 p-4 transition-all duration-200 ease-out ${metric ? "cursor-pointer hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md" : ""}`}>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}><Icon size={18} /></div>
        <div className="min-w-0">
          <div className="font-head text-lg font-semibold tabular-nums text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
        {metric && <HelpCircle size={14} className="absolute right-2 top-2 text-slate-300 transition-colors group-hover:text-amber-500" />}
      </Card>
    );
  };

  return (
    <div className="space-y-6 fade-in" data-testid="apartment-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/apartments")} data-testid="back-btn"><ArrowLeft size={16} /></Button>
          <PhotoManager apartmentId={id} imageUrl={a.image_url} onChange={load} />
          <div>
            <div className="flex items-center gap-2">
              {a.code && <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">{a.code}</span>}
              <h1 className="font-head text-xl font-semibold text-slate-900">{a.name}</h1>
              <StatusBadge value={a.status} />
            </div>
            <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} /> {a.zone || a.address || "—"} · {d.owner_name}</p>
            {a.pipeline_id && <p className="mt-0.5 cursor-pointer text-xs font-medium text-indigo-600 hover:underline" onClick={() => navigate(`/pipeline?open=${a.pipeline_id}`)} data-testid="apt-from-pipeline">📂 Derivato dalla trattativa · apri scheda commerciale</p>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button data-testid="dup-config-btn" variant="outline" size="sm" className="gap-1.5 border-cyan-200 text-cyan-700 hover:bg-cyan-50" onClick={openDupConfig}>
              <Copy size={15} /> Duplica configurazione
            </Button>
            <Button data-testid="duplicate-detail-btn" variant="outline" size="sm" className="gap-1.5 border-indigo-200 text-indigo-600 hover:bg-indigo-50" onClick={duplicate}>
              <Copy size={15} /> {t("duplicate")}
            </Button>
          </div>
        )}
      </div>

      {/* Config completion bar */}
      {d.config && (
        <Card className="flex items-center gap-4 border-slate-200 p-4" data-testid="config-completion">
          <div className="flex-1">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-semibold uppercase tracking-wide text-slate-500">Completamento configurazione</span>
              <span className={`font-head font-semibold ${d.config.pct === 100 ? "text-emerald-600" : "text-amber-600"}`}>{d.config.pct}% · {d.config.done}/{d.config.total}</span>
            </div>
            <Progress value={d.config.pct} className={`h-2 ${d.config.pct === 100 ? "[&>div]:bg-emerald-500" : "[&>div]:bg-amber-500"}`} />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {d.config.items.filter((it) => !it.done).map((it) => (
                <span key={it.key} data-testid={`config-missing-${it.key}`} className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-600">{it.label}</span>
              ))}
              {d.config.pct === 100 && <span className="text-[11px] text-emerald-600">✓ Configurazione completa</span>}
            </div>
          </div>
        </Card>
      )}

      <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.k} data-testid={`detail-tab-${tb.k}`} onClick={() => setTab(tb.k)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === tb.k ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="detail-overview">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500">{t("economicDashboard")} · <span className="font-medium text-slate-700">{d.period.label}</span> · <span className="text-amber-600">clicca un KPI per il dettaglio "Perché?"</span></div>
            <div className="flex items-center gap-2" data-testid="period-selector">
              <Select value={String(period.month)} onValueChange={(v) => setPeriod((p) => ({ ...p, month: Number(v) }))}>
                <SelectTrigger className="h-8 w-32" data-testid="period-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS_IT.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={String(period.year)} onValueChange={(v) => setPeriod((p) => ({ ...p, year: Number(v) }))}>
                <SelectTrigger className="h-8 w-24" data-testid="period-year"><SelectValue /></SelectTrigger>
                <SelectContent>{[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {kpi(Wallet, t("grossRevenue"), eur(e.gross_revenue), "bg-primary text-primary-foreground", "gross_revenue")}
            {kpi(TrendingUp, t("netRevenue"), eur(e.net_revenue), "bg-emerald-100 text-emerald-700", "net_revenue")}
            {kpi(Coins, t("directCosts"), eur(e.direct_costs), "bg-rose-100 text-rose-700", "direct_costs")}
            {kpi(TrendingUp, t("margin"), eur(e.margin), e.margin >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700", "margin")}
            {kpi(Gauge, "ADR", eur(e.adr), "bg-indigo-100 text-indigo-700", "adr")}
            {kpi(Activity, "RevPAR", eur(e.revpar), "bg-sky-100 text-sky-700", "revpar")}
            {kpi(BedDouble, t("occupancy"), `${e.occupancy}%`, "bg-cyan-100 text-cyan-700", "occupancy")}
            {kpi(Percent, t("otaCommission"), eur(e.ota_commission), "bg-violet-100 text-violet-700", "ota_commission")}
          </div>
          <Card className="border-slate-200 p-5">
            <h3 className="mb-4 font-head font-semibold text-slate-900">{t("trend12")}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.trend} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => eur(v)} />
                <Bar dataKey="revenue" name={t("grossRevenue")} fill="#1e293b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="border-slate-200 p-5">
            <h3 className="mb-3 font-head font-semibold text-slate-900">{t("apartments")}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div className="flex items-center gap-2 text-slate-600"><Users size={15} /> {a.max_guests} {t("guests")}</div>
              <div className="flex items-center gap-2 text-slate-600"><BedDouble size={15} /> {a.bedrooms}</div>
              <div className="flex items-center gap-2 text-slate-600"><Bath size={15} /> {a.bathrooms}</div>
              <div className="flex items-center gap-2 text-slate-600"><KeyRound size={15} /> {a.keybox_code || "—"}</div>
              <div className="col-span-2 flex items-center gap-2 text-slate-600"><Wifi size={15} /> {a.wifi_name || "—"} · <span className="font-mono">{a.wifi_password}</span></div>
            </div>
            {a.notes && <p className="mt-3 text-sm text-slate-600">{a.notes}</p>}
          </Card>
        </div>
      )}

      {tab === "info" && (
        <Card className="border-slate-200 p-5" data-testid="detail-info">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-head font-semibold text-slate-900">Anagrafica immobile</h3>
            {isAdmin && !editInfo && <Button size="sm" variant="outline" data-testid="edit-info-btn" onClick={startEdit}>{t("edit")}</Button>}
            {editInfo && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditInfo(false)}>{t("cancel")}</Button>
                <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="save-info-btn" onClick={saveInfo}>{t("save")}</Button>
              </div>
            )}
          </div>
          {!editInfo ? (
            <div data-testid="info-view">
              <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="access-pair-view">
                <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-4">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Keybox</div>
                  <div className="flex items-center gap-2 font-mono text-xl font-bold text-slate-900"><KeyRound size={18} className="text-slate-400" /> {a.keybox_code || "—"}</div>
                </div>
                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">Apertura portone</div>
                  {a.auto_gate_phone
                    ? <a href={`tel:${a.auto_gate_phone}`} data-testid="access-gate-phone" className="flex items-center gap-2 text-xl font-bold text-emerald-700"><Phone size={18} /> {a.auto_gate_phone}</a>
                    : <div className="text-xl font-bold text-slate-300">—</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                {[
                  ["Nome", a.name], ["Indirizzo", a.address || "—"], ["Zona", a.zone || "—"],
                  ["Ospiti max", a.max_guests], ["Camere", a.bedrooms], ["Bagni", a.bathrooms],
                  ["CIN", a.cin || "—"], ["CIR", a.cir || "—"],
                  ["Wi-Fi", a.wifi_name || "—"], ["Password Wi-Fi", a.wifi_password || "—"],
                  ["Coordinate", (a.latitude && a.longitude) ? `${a.latitude}, ${a.longitude}` : "—"],
                  ["Link documenti (Tally)", a.tally_documents_url || "—"], ["Welcome Guide", a.welcome_guide_url || "—"],
                  ["Video", a.video_url || "—"],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between border-b border-slate-100 py-1.5">
                    <span className="text-slate-500">{l}</span><span className="truncate pl-3 font-medium text-slate-800">{String(v)}</span>
                  </div>
                ))}
                {a.notes && <div className="col-span-full pt-2 text-slate-600">{a.notes}</div>}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="info-edit">
              <div className="col-span-full grid grid-cols-1 gap-4 rounded-xl border-2 border-emerald-100 bg-emerald-50/40 p-4 sm:grid-cols-2" data-testid="access-pair-edit">
                <Field label="Keybox"><Input data-testid="pair-keybox-input" value={infoForm.keybox_code || ""} onChange={(e) => setF("keybox_code", e.target.value)} /></Field>
                <Field label="Apertura portone (numero dedicato · formato internazionale)">
                  <Input data-testid="pair-gate-input" value={infoForm.auto_gate_phone || ""} placeholder="+352602141965143"
                    onChange={(e) => { const v = e.target.value; setF("auto_gate_phone", v); if (v.trim()) setF("auto_gate_enabled", true); }} />
                </Field>
              </div>
              <Field label="Nome"><Input data-testid="info-name" value={infoForm.name || ""} onChange={(e) => setF("name", e.target.value)} /></Field>
              <Field label="Indirizzo"><Input value={infoForm.address || ""} onChange={(e) => setF("address", e.target.value)} /></Field>
              <Field label="Zona"><Input value={infoForm.zone || ""} onChange={(e) => setF("zone", e.target.value)} /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Ospiti"><Input type="number" value={infoForm.max_guests || 0} onChange={(e) => setF("max_guests", +e.target.value)} /></Field>
                <Field label="Camere"><Input type="number" value={infoForm.bedrooms || 0} onChange={(e) => setF("bedrooms", +e.target.value)} /></Field>
                <Field label="Bagni"><Input type="number" value={infoForm.bathrooms || 0} onChange={(e) => setF("bathrooms", +e.target.value)} /></Field>
              </div>
              <Field label="CIN"><Input data-testid="info-cin" value={infoForm.cin || ""} onChange={(e) => setF("cin", e.target.value)} /></Field>
              <Field label="CIR"><Input value={infoForm.cir || ""} onChange={(e) => setF("cir", e.target.value)} /></Field>
              <Field label="Wi-Fi SSID"><Input value={infoForm.wifi_name || ""} onChange={(e) => setF("wifi_name", e.target.value)} /></Field>
              <Field label="Password Wi-Fi"><Input value={infoForm.wifi_password || ""} onChange={(e) => setF("wifi_password", e.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Latitudine"><Input value={infoForm.latitude || ""} onChange={(e) => setF("latitude", e.target.value)} /></Field>
                <Field label="Longitudine"><Input value={infoForm.longitude || ""} onChange={(e) => setF("longitude", e.target.value)} /></Field>
              </div>
              <Field label="Link documenti (Tally)"><Input value={infoForm.tally_documents_url || ""} onChange={(e) => setF("tally_documents_url", e.target.value)} /></Field>
              <Field label="Welcome Guide URL"><Input value={infoForm.welcome_guide_url || ""} onChange={(e) => setF("welcome_guide_url", e.target.value)} /></Field>
              <Field label="Video URL"><Input value={infoForm.video_url || ""} onChange={(e) => setF("video_url", e.target.value)} /></Field>
              <Field label="Note" className="sm:col-span-2"><Textarea value={infoForm.notes || ""} onChange={(e) => setF("notes", e.target.value)} /></Field>
            </div>
          )}
        </Card>
      )}

      {tab === "info" && (
        <Card className="mt-4 border-slate-200 p-5" data-testid="detail-autogate">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-head font-semibold text-slate-900">
              <DoorOpen size={18} className="text-indigo-600" /> Apertura automatica portone
            </h3>
            {!editInfo && (
              <span data-testid="autogate-status" className={`rounded-full px-2.5 py-1 text-xs font-medium ${(a.auto_gate_enabled || a.smart_access_enabled) ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {(a.auto_gate_enabled || a.smart_access_enabled) ? "Attivo" : "Non attivo"}
              </span>
            )}
          </div>

          {!editInfo ? (
            (a.auto_gate_enabled || a.auto_gate2_enabled || a.smart_access_enabled) ? (
              <div className="space-y-4 text-sm" data-testid="autogate-view">
                {a.auto_gate_enabled && (
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700"><Phone size={13} /> Apertura tramite chiamata (SIM)</div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                      <div className="flex items-center justify-between border-b border-emerald-100 py-1.5">
                        <span className="text-slate-500">Numero da chiamare</span>
                        {a.auto_gate_phone
                          ? <a href={`tel:${a.auto_gate_phone}`} className="inline-flex items-center gap-1.5 font-medium text-emerald-700" data-testid="autogate-phone"><Phone size={14} /> {a.auto_gate_phone}</a>
                          : <span className="font-medium text-red-500">Numero mancante</span>}
                      </div>
                      <div className="flex items-center justify-between border-b border-emerald-100 py-1.5">
                        <span className="text-slate-500">Tempo apertura</span>
                        <span className="font-medium text-slate-800">{a.auto_gate_time || "—"}</span>
                      </div>
                      <div className="flex items-center justify-between border-b border-emerald-100 py-1.5">
                        <span className="text-slate-500">Modalità</span>
                        <span className="font-medium text-slate-800">{({ telefono: "Telefono", smart: "Smart access", entrambe: "Entrambe" })[a.auto_gate_mode || "telefono"]}</span>
                      </div>
                      <div className="flex items-center justify-between border-b border-emerald-100 py-1.5">
                        <span className="text-slate-500">Stato test</span>
                        <span data-testid="autogate-test-badge" className={`rounded-full px-2 py-0.5 text-xs font-medium ${({ non_configurato: "bg-slate-100 text-slate-500", configurato: "bg-amber-100 text-amber-700", testato: "bg-sky-100 text-sky-700", operativo: "bg-emerald-100 text-emerald-700" })[a.auto_gate_test_status || "non_configurato"]}`}>{({ non_configurato: "Non configurato", configurato: "Configurato", testato: "Testato", operativo: "Operativo" })[a.auto_gate_test_status || "non_configurato"]}</span>
                      </div>
                      {a.auto_gate_provider && (
                        <div className="flex items-center justify-between border-b border-emerald-100 py-1.5">
                          <span className="text-slate-500">Provider (futuro)</span>
                          <span className="font-medium capitalize text-slate-800">{a.auto_gate_provider}</span>
                        </div>
                      )}
                    </div>
                    {a.auto_gate_notes && <p className="mt-2 text-slate-600"><span className="text-slate-400">Note operative: </span>{a.auto_gate_notes}</p>}
                  </div>
                )}
                {a.auto_gate2_enabled && (
                  <div className="rounded-xl border border-cyan-100 bg-cyan-50/50 p-4" data-testid="autogate2-view">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-cyan-700"><DoorOpen size={13} /> Secondo portone · "Apri Portone Interno"</div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                      <div className="flex items-center justify-between border-b border-cyan-100 py-1.5">
                        <span className="text-slate-500">Numero da chiamare</span>
                        {a.auto_gate2_phone
                          ? <a href={`tel:${a.auto_gate2_phone}`} className="inline-flex items-center gap-1.5 font-medium text-cyan-700" data-testid="autogate2-phone"><Phone size={14} /> {a.auto_gate2_phone}</a>
                          : <span className="font-medium text-red-500">Numero mancante</span>}
                      </div>
                      <div className="flex items-center justify-between border-b border-cyan-100 py-1.5">
                        <span className="text-slate-500">Tempo apertura</span>
                        <span className="font-medium text-slate-800">{a.auto_gate2_time || "—"}</span>
                      </div>
                    </div>
                    {a.auto_gate2_notes && <p className="mt-2 text-slate-600"><span className="text-slate-400">Note operative: </span>{a.auto_gate2_notes}</p>}
                  </div>
                )}
                {a.smart_access_enabled && (
                  <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-4" data-testid="smartaccess-view">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-sky-700"><KeyRound size={13} /> Accesso smart {a.smart_access_provider ? `· ${a.smart_access_provider}` : ""}</div>
                    <div className="flex items-center justify-between border-b border-sky-100 py-1.5">
                      <span className="text-slate-500">Link/Deep link</span>
                      {a.smart_access_url ? <a href={a.smart_access_url} target="_blank" rel="noreferrer" className="truncate font-medium text-sky-700">{a.smart_access_url}</a> : <span className="font-medium text-red-500">URL mancante</span>}
                    </div>
                  </div>
                )}
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-slate-700" data-testid="autogate-message">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-600">Testo informativo per l'ospite</div>
                  {AUTO_GATE_MESSAGE}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400" data-testid="autogate-view">Nessuna modalità di apertura attiva per questo immobile.</p>
            )
          ) : (
            <div className="space-y-4" data-testid="autogate-edit">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><Phone size={14} /> Apertura tramite chiamata (SIM)</span>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!infoForm.auto_gate_enabled} onCheckedChange={(v) => setF("auto_gate_enabled", v)} data-testid="autogate-enabled-inline" /> Attiva</label>
                </div>
                {infoForm.auto_gate_enabled ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Numero telefonico da chiamare"><Input data-testid="autogate-phone-input" value={infoForm.auto_gate_phone || ""} placeholder="+352602141965143" onChange={(e) => setF("auto_gate_phone", e.target.value)} /></Field>
                      <Field label="Tempo apertura (facoltativo · secondi)"><Input data-testid="autogate-time-input" value={infoForm.auto_gate_time || ""} placeholder="Es. 5" onChange={(e) => setF("auto_gate_time", e.target.value)} /></Field>
                    </div>
                    <p className="-mt-1 text-[11px] text-emerald-600">🔗 Numero sincronizzato con il campo "Apertura portone" dell'Anagrafica: modificandolo qui o lì resta sempre allineato.</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Field label="Modalità">
                        <Select value={infoForm.auto_gate_mode || "telefono"} onValueChange={(v) => setF("auto_gate_mode", v)}>
                          <SelectTrigger className="h-9" data-testid="autogate-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>{[["telefono", "Telefono"], ["smart", "Smart access"], ["entrambe", "Entrambe"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Provider (futuro)">
                        <Select value={infoForm.auto_gate_provider || "nessuno"} onValueChange={(v) => setF("auto_gate_provider", v === "nessuno" ? "" : v)}>
                          <SelectTrigger className="h-9" data-testid="autogate-provider"><SelectValue /></SelectTrigger>
                          <SelectContent>{[["nessuno", "—"], ["vikey", "Vikey"], ["nuki", "Nuki"], ["ttlock", "TTLock"], ["yale", "Yale"], ["altro", "Altro"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Stato test">
                        <Select value={infoForm.auto_gate_test_status || "non_configurato"} onValueChange={(v) => setF("auto_gate_test_status", v)}>
                          <SelectTrigger className="h-9" data-testid="autogate-test-status"><SelectValue /></SelectTrigger>
                          <SelectContent>{[["non_configurato", "Non configurato"], ["configurato", "Configurato"], ["testato", "Testato"], ["operativo", "Operativo"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field label="Note operative (facoltative)"><Textarea data-testid="autogate-notes-input" value={infoForm.auto_gate_notes || ""} onChange={(e) => setF("auto_gate_notes", e.target.value)} /></Field>
                  </div>
                ) : <p className="text-xs text-slate-400">Attiva per configurare il numero da chiamare (chiamata dalla SIM dell'ospite, nessun WhatsApp/VoIP).</p>}
              </div>
              <div className="rounded-xl border border-cyan-100 bg-cyan-50/40 p-4" data-testid="autogate2-block">
                <div className="mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold text-cyan-700"><DoorOpen size={14} /> Secondo portone · "Apri Portone Interno"</span>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!infoForm.auto_gate2_enabled} onCheckedChange={(v) => setF("auto_gate2_enabled", v)} data-testid="autogate2-enabled" /> Attiva</label>
                </div>
                {infoForm.auto_gate2_enabled ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Numero telefonico secondo portone"><Input data-testid="autogate2-phone-input" value={infoForm.auto_gate2_phone || ""} placeholder="+3934xxxxxxx" onChange={(e) => setF("auto_gate2_phone", e.target.value)} /></Field>
                      <Field label="Tempo apertura (facoltativo · secondi)"><Input data-testid="autogate2-time-input" value={infoForm.auto_gate2_time || ""} placeholder="Es. 5" onChange={(e) => setF("auto_gate2_time", e.target.value)} /></Field>
                    </div>
                    <Field label="Note operative (facoltative)"><Textarea data-testid="autogate2-notes-input" value={infoForm.auto_gate2_notes || ""} onChange={(e) => setF("auto_gate2_notes", e.target.value)} /></Field>
                    <p className="-mt-1 text-[11px] text-cyan-600">Pulsante indipendente nel Guest Portal per edifici con doppio ingresso. Se disattivato non viene mostrato all'ospite.</p>
                  </div>
                ) : <p className="text-xs text-slate-400">Attiva per aggiungere un secondo pulsante di chiamata (ingresso interno/secondario), con numero indipendente dal portone principale.</p>}
              </div>
              <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold text-sky-700"><KeyRound size={14} /> Accesso smart (Vikey, Nuki, Yale, TTLock…)</span>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!infoForm.smart_access_enabled} onCheckedChange={(v) => { setF("smart_access_enabled", v); if (v && !infoForm.smart_access_provider) setF("smart_access_provider", "vikey"); }} data-testid="smartaccess-enabled" /> Attiva</label>
                </div>
                {infoForm.smart_access_enabled ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Provider">
                      <Select value={infoForm.smart_access_provider || "vikey"} onValueChange={(v) => setF("smart_access_provider", v)}>
                        <SelectTrigger className="h-9" data-testid="smartaccess-provider"><SelectValue /></SelectTrigger>
                        <SelectContent>{["vikey", "nuki", "yale", "ttlock", "altro"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="URL / Deep link apertura"><Input data-testid="smartaccess-url-input" value={infoForm.smart_access_url || ""} placeholder="https://… oppure deep link app" onChange={(e) => setF("smart_access_url", e.target.value)} /></Field>
                  </div>
                ) : <p className="text-xs text-slate-400">Predisposizione futura: attiva e inserisci l'URL/deep link del provider. L'integrazione API completa (es. Vikey) verrà collegata in seguito.</p>}
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4" data-testid="doorlock-config">
                <div className="mb-2 flex items-center gap-2">
                  <DoorOpen size={15} className="text-emerald-600" />
                  <span className="text-sm font-semibold text-slate-800">Serratura smart · Porta appartamento</span>
                  <label className="ml-auto flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!infoForm.door_lock_enabled} onCheckedChange={(v) => setF("door_lock_enabled", v)} data-testid="doorlock-enabled" /> Attiva</label>
                </div>
                <p className="mb-3 text-xs text-slate-400">Opzionale: mostra all'ospite il pulsante «Apri porta appartamento» e/o il codice serratura nella sezione Check-in. Se lasciato vuoto, la pagina ospite resta invariata.</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Provider (opz.)">
                    <Select value={infoForm.door_lock_provider || "nuki"} onValueChange={(v) => setF("door_lock_provider", v)}>
                      <SelectTrigger className="h-9" data-testid="doorlock-provider"><SelectValue /></SelectTrigger>
                      <SelectContent>{["nuki", "yale", "ttlock", "igloohome", "altro"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="URL / Deep link apertura"><Input data-testid="doorlock-url-input" value={infoForm.door_lock_url || ""} placeholder="https://… oppure deep link app" onChange={(e) => setF("door_lock_url", e.target.value)} /></Field>
                  <Field label="Codice serratura (opz.)"><Input data-testid="doorlock-code-input" value={infoForm.door_lock_code || ""} placeholder="es. 4821" onChange={(e) => setF("door_lock_code", e.target.value)} /></Field>
                </div>
              </div>
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-slate-700">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-600">Testo informativo standard (mostrato all'ospite)</div>
                {AUTO_GATE_MESSAGE}
              </div>
            </div>
          )}
        </Card>
      )}

      {tab === "documents" && (
        <Card className="border-slate-200 p-5" data-testid="detail-documents">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Categoria">
                <Select value={docMeta.category} onValueChange={(v) => setDocMeta((m) => ({ ...m, category: v }))}>
                  <SelectTrigger className="h-9 w-44" data-testid="doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Contratto", "Assicurazione", "Certificazioni", "Planimetria", "APE", "Registrazione", "Fatture", "Manuali", "Altro"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Scadenza (opz.)"><Input type="date" className="h-9 w-40" data-testid="doc-expiry" value={docMeta.expiry_date} onChange={(e) => setDocMeta((m) => ({ ...m, expiry_date: e.target.value }))} /></Field>
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={uploadDoc} data-testid="doc-file-input" />
            <Button data-testid="upload-doc-btn" onClick={() => fileRef.current?.click()} className="gap-2 bg-primary hover:bg-primary/90"><Upload size={16} /> {t("addDocument")}</Button>
          </div>
          <div className="space-y-2">
            {(a.documents || []).map((doc) => {
              const exp = doc.expiry_date ? new Date(doc.expiry_date) : null;
              const expired = exp && exp < new Date();
              const soon = exp && !expired && (exp - new Date()) / 86400000 < 30;
              return (
                <div key={doc.id} data-testid={`doc-${doc.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                  <a href={`${BACKEND}${doc.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-800 hover:text-primary">
                    <FileText size={16} className="text-rose-600" /> {doc.name}
                    {doc.category && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{doc.category}</span>}
                    {exp && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${expired ? "bg-rose-100 text-rose-700" : soon ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-600"}`}>{expired ? "Scaduto" : "Scade"} {fmtDate(doc.expiry_date)}</span>}
                  </a>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">{fmtDate(doc.uploaded_at)}</span>
                    <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`del-doc-${doc.id}`} onClick={() => delDoc(doc.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              );
            })}
            {(a.documents || []).length === 0 && <p className="text-sm text-slate-400">{t("noDocuments")}</p>}
          </div>
        </Card>
      )}

      {tab === "costs" && (
        <div className="space-y-6" data-testid="detail-costs">
          <EconomicDataEditor apartmentId={id} onChanged={load} />
          <div>
            <h3 className="mb-3 font-head font-semibold text-slate-900">Costi variabili (bollette, interventi, spese una-tantum)</h3>
            <ExpenseManager apartmentId={id} onChanged={load} />
          </div>
        </div>
      )}

      {tab === "bookings" && (
        <Card className="overflow-hidden border-slate-200" data-testid="detail-bookings">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-3">{t("guestName")}</th><th className="px-4 py-3">{t("checkIn")}</th><th className="px-4 py-3">{t("checkOut")}</th><th className="px-4 py-3">{t("channel")}</th><th className="px-4 py-3">{t("totalAmount")}</th><th className="px-4 py-3">{t("paymentStatus")}</th></tr>
            </thead>
            <tbody>
              {d.bookings.map((b) => (
                <tr key={b.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.guest_name}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_in)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(b.check_out)}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{b.channel}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(b.total_amount)}</td>
                  <td className="px-4 py-3"><StatusBadge value={b.payment_status} /></td>
                </tr>
              ))}
              {d.bookings.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {tab === "maintenance" && (
        <Card className="overflow-hidden border-slate-200" data-testid="detail-maintenance">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-3">{t("issue")}</th><th className="px-4 py-3">{t("priority")}</th><th className="px-4 py-3">{t("status")}</th><th className="px-4 py-3">{t("finalCost")}</th></tr>
            </thead>
            <tbody>
              {d.maintenance.map((m) => (
                <tr key={m.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-800">{m.issue}</td>
                  <td className="px-4 py-3"><StatusBadge value={m.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge value={m.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{eur(m.final_cost || m.estimated_cost)}</td>
                </tr>
              ))}
              {d.maintenance.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {tab === "media" && <MediaCenterTab apartmentId={id} onMainPhotoChange={load} mainMediaId={a.image_media_id} />}
      {tab === "welcome" && <WelcomeCenterManager aptId={id} />}
      {tab === "inventory" && <InventoryTab apartmentId={id} />}
      {tab === "utilities" && <UtilitiesTab apartmentId={id} />}
      {tab === "integrations" && <IntegrationsTab apartmentId={id} />}
      {tab === "automations" && <AutomationsTab apartmentId={id} />}

      {tab === "report" && (
        <Card className="border-slate-200 p-5" data-testid="detail-report">
          <h3 className="mb-4 font-head font-semibold text-slate-900">{d.period.label}</h3>
          <div className="space-y-2 text-sm">
            {[["grossRevenue", e.gross_revenue], ["otaCommission", e.ota_commission], ["stripeCommission", e.stripe_commission], ["netRevenue", e.net_revenue], ["directCosts", e.direct_costs]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">{t(k)}</span><span className="font-medium text-slate-900">{eur(v)}</span></div>
            ))}
            <div className="flex justify-between rounded-lg bg-emerald-50 px-3 py-3"><span className="font-semibold text-emerald-800">{t("margin")}</span><span className={`font-bold ${e.margin >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(e.margin)}</span></div>
          </div>
        </Card>
      )}

      {tab === "history" && (
        <Card className="border-slate-200 p-5" data-testid="detail-history">
          <h3 className="mb-4 font-head font-semibold text-slate-900">Cronologia immobile</h3>
          {hist === null && <div className="text-sm text-slate-400">Caricamento…</div>}
          {hist && hist.length === 0 && <div className="text-sm text-slate-400">Nessun evento registrato per questo immobile.</div>}
          <div className="relative space-y-4 pl-5">
            {hist && hist.length > 0 && <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" />}
            {(hist || []).map((h) => (
              <div key={h.id} data-testid={`hist-${h.id}`} className="relative">
                <div className="absolute -left-[15px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500" />
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{h.action_label}</span>
                  <span className="text-xs text-slate-400">{new Date(h.at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {h.object_label}{h.amount != null ? ` · ${eur(h.amount)}` : ""}{h.reason ? ` · ${h.reason}` : ""} · <span className="text-slate-400">{h.user_email}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <WhySheet
        apartmentId={id} metric={why.metric} year={period.year} month={period.month}
        open={why.open} onOpenChange={(o) => setWhy((w) => ({ ...w, open: o }))}
        onNavigate={(t2) => setTab(t2)}
      />

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-lg" data-testid="dup-config-dialog">
          <DialogHeader>
            <DialogTitle>Duplica configurazione</DialogTitle>
            <DialogDescription>
              Copia le sezioni scelte da <b>{a.name}</b> ({a.code}) su un altro immobile. Verranno sovrascritte solo le sezioni selezionate.
            </DialogDescription>
          </DialogHeader>

          {dupStep === "select" ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Immobile di destinazione</label>
                <Select value={dupTarget} onValueChange={setDupTarget}>
                  <SelectTrigger data-testid="dup-target-select"><SelectValue placeholder="Seleziona un immobile…" /></SelectTrigger>
                  <SelectContent>
                    {dupTargets.map((x) => <SelectItem key={x.id} value={x.id} data-testid={`dup-target-${x.code}`}>{x.code} · {x.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sezioni da copiare</label>
                  <button onClick={toggleAll} data-testid="dup-toggle-all" className="text-xs font-medium text-cyan-700 hover:underline">{allSelected ? "Deseleziona tutto" : "Duplica completa"}</button>
                </div>
                <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-lg border border-slate-100 p-2">
                  {dupSectionsCat.map((s) => (
                    <label key={s.key} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`dup-sec-${s.key}`}>
                      <Checkbox checked={dupSel.includes(s.key)} onCheckedChange={() => toggleSec(s.key)} />
                      <span className="text-slate-700">{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDupOpen(false)} data-testid="dup-cancel-btn">Annulla</Button>
                <Button disabled={!dupTarget || dupSel.length === 0} onClick={() => setDupStep("confirm")} data-testid="dup-next-btn" className="bg-cyan-600 hover:bg-cyan-700">Continua</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="dup-confirm-box">
                Stai per sovrascrivere <b>{dupSel.length}</b> sezioni su <b>{dupTargetName?.name}</b> ({dupTargetName?.code}). L'operazione non è reversibile. Confermi?
                <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">
                  {dupSel.map((k) => <li key={k}>{dupSectionsCat.find((s) => s.key === k)?.label}</li>)}
                </ul>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDupStep("select")} data-testid="dup-back-btn">Indietro</Button>
                <Button disabled={dupBusy} onClick={runDupConfig} data-testid="dup-confirm-btn" className="bg-cyan-600 hover:bg-cyan-700">{dupBusy ? "Copia in corso…" : "Conferma e copia"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
